import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useScreenNavigation } from '../hooks/useScreenNavigation';
import { useScreenContext } from '../contexts/ScreenContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { NavigationButtons } from './NavigationButtons';
import { LandingScreen } from './screens/LandingScreen';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { ProcessingScreen } from './screens/ProcessingScreen';
import { SuccessScreen } from './screens/SuccessScreen';
import { UseCasesScreen } from './screens/UseCasesScreen';
import { AccountHomeScreen } from './screens/AccountHomeScreen';
import { FundingScreen } from './screens/FundingScreen';
import { WithdrawFiatScreen } from './screens/WithdrawFiatScreen';
import { ConclusionScreen } from './screens/ConclusionScreen';

export const DemoContainer: React.FC = () => {
  const { screenGraph } = useScreenContext();
  const { errorState, handleError, clearError } = useErrorHandler();
  const {
    currentScreen,
    canGoBack,
    canGoNext,
    isLoading,
    goToNext,
    goToPrevious
  } = useScreenNavigation(screenGraph.screens);

  // Track active tab for FundingScreen
  const [fundingActiveTab, setFundingActiveTab] = useState<string>('create-account');
  
  // Track active tab for WithdrawFiatScreen
  const [withdrawFiatActiveTab, setWithdrawFiatActiveTab] = useState<'withdraw' | 'elixir-transfer' | 'complete'>('withdraw');

  const handleNext = async () => {
    try {
      clearError();
      await goToNext();
    } catch (error) {
      handleError(error as Error);
    }
  };

  const renderScreen = () => {
    switch (currentScreen.type) {
      case 'landing':
        return <LandingScreen screen={currentScreen} />;
      case 'onboarding':
        return <OnboardingScreen screen={currentScreen} onNext={handleNext} />;
      case 'processing':
        return <ProcessingScreen screen={currentScreen} onComplete={goToNext} />;
      case 'success':
        return <SuccessScreen screen={currentScreen} onNext={handleNext} />;
      case 'use-cases':
        return <UseCasesScreen screen={currentScreen} />;
      case 'account-home':
        return <AccountHomeScreen screen={currentScreen} onNext={handleNext} />;
      case 'funding':
        return <FundingScreen screen={currentScreen} onTabChange={setFundingActiveTab} />;
      case 'withdraw-fiat':
        return <WithdrawFiatScreen screen={currentScreen} onTabChange={setWithdrawFiatActiveTab} />;
      case 'conclusion':
        return <ConclusionScreen screen={currentScreen} />;
      default:
        return <LandingScreen screen={currentScreen} />;
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Main Content */}
      <motion.div
        key={currentScreen.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="relative z-10"
      >
        {renderScreen()}
      </motion.div>

      {/* Error Display */}
      {errorState.hasError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 max-w-sm"
        >
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-xs">!</span>
            </div>
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-xs text-red-600">{errorState.error}</p>
            </div>
            <button
              onClick={clearError}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </motion.div>
      )}

      {/* Navigation */}
      <NavigationButtons
        canGoBack={canGoBack}
        canGoNext={
          currentScreen.type === 'funding' 
            ? fundingActiveTab === 'receive-eth' 
            : currentScreen.type === 'withdraw-fiat'
            ? withdrawFiatActiveTab === 'complete'
            : canGoNext
        }
        isLoading={isLoading}
        onPrevious={goToPrevious}
        onNext={handleNext}
      />
    </div>
  );
};
